import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { FormProvider, useForm } from 'react-hook-form';
import { useTheme } from 'styled-components/macro';

import { useMessages } from '../../contexts/MessagesContext';
import { NewTicketMessageFormValuesT } from './declarations';
import useMessageForm from './hooks/useMessageForm';
import {
  CloseIconBtnStyled,
  FormStyled,
  InputImageWrapStyled,
  InputMessageWrapStyled,
  InputTextStyled,
  PopupContainerStyled,
  SendMessageBtnStyled,
  StyledDragDropInput,
  getPopupContentStyle,
} from './MessageForm.styled';
import validationSchema from './validationSchema';
import Button from 'components/Button/Button';
import ErrorSection from 'components/ErrorSection/ErrorSection';
import FlexContainer from 'components/FlexContainer/FlexContainer';
import Popup from 'components/Popup/Popup';
import Typography from 'components/Typography/Typography';
import { useResponsive } from 'contexts/ResponsiveContext';
import { NoopT } from 'declarations/types/common';
import { CloseIcon, PlusIcon, SendIcon } from 'icons';

export type MessageFormPropsT = {
  roofTopName: string;
  requiresAttachment: boolean;
  assignToWarrCloud: boolean;
  handleOpenAddAttachmentPopup: NoopT;
  isTicketClosed: boolean;
};

const MessageForm: FC<MessageFormPropsT> = ({
  requiresAttachment,
  assignToWarrCloud,
  handleOpenAddAttachmentPopup,
  isTicketClosed,
}) => {
  /**
   * Hooks
   */
  const {
    unsavedMessage,
    isCreatingMessage,
    isMessageCreationError,
    createMessageErrorMessage,
    createMessageValidationErrorMessage,
  } = useMessages();
  const { handleSubmitForm } = useMessageForm(assignToWarrCloud);
  const fromMethods = useForm<NewTicketMessageFormValuesT>({
    defaultValues: {
      message: '',
      file: undefined,
    },
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
  });

  /**
   * Variables
   */
  const { handleSubmit, reset } = fromMethods;
  const [previewImage, setPreviwImage] = useState<string | null>(null);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const theme = useTheme();
  const warningPopupRef = useRef(null);
  const { isMobile } = useResponsive();

  const { getRootProps, getInputProps } = useDropzone({
    multiple: false,
    onDropAccepted: (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (file.type === 'image/jpeg' || file.type === 'image/png') {
        fromMethods.setValue('file', file);

        const reader = new FileReader();
        reader.onload = e => setPreviwImage(e.target?.result as string);
        reader.readAsDataURL(file);
      } else {
        alert('Only JPEG and PNG files are allowed.');
      }
    },
    noClick: true,
    disabled: !requiresAttachment,
  });

  const handleImagePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    if (!requiresAttachment) {
      return;
    }
    const pastedData = event.clipboardData.items;
    if (pastedData) {
      const imageItem = pastedData[0];
      if (imageItem.kind === 'file') {
        const file = imageItem.getAsFile();
        if (file?.type === 'image/jpeg' || file?.type === 'image/png') {
          fromMethods.setValue('file', file);
          const reader = new FileReader();
          reader.onload = e => setPreviwImage(e.target?.result as string);
          reader.readAsDataURL(file);
        }
      }
    }
  };

  const handleRemoveImage = () => {
    setPreviwImage(null);
  };

  const handleInputFocus = () => {
    if (isTicketClosed) {
      setIsPopupVisible(true);
    }
  };

  const handlePopupConfirm = () => {
    setIsPopupVisible(false);
  };

  /**
   * Effects
   */
  useEffect(() => {
    if (unsavedMessage) {
      reset();
    }
  }, [unsavedMessage, reset]);

  return (
    <>
      {isMessageCreationError ? (
        <FlexContainer direction="column" align="center" justify="center">
          <ErrorSection
            errorDetails={createMessageErrorMessage || createMessageValidationErrorMessage}
          />
        </FlexContainer>
      ) : (
        <FormProvider {...fromMethods}>
          <FormStyled onSubmit={handleSubmit(handleSubmitForm)}>
            <Button
              disabled={!requiresAttachment}
              type="button"
              size="xl"
              variant="outline"
              icon={<PlusIcon />}
              onClick={handleOpenAddAttachmentPopup}
            >
              Attach
            </Button>
            <InputMessageWrapStyled onPaste={handleImagePaste} {...getRootProps()}>
              <InputTextStyled
                rows={1}
                name="message"
                placeholder={`Send a message to WarrCloud`}
                disabled={isCreatingMessage}
                onFocus={isTicketClosed ? handleInputFocus : undefined}
              />
              <Popup
                offsetY={4}
                arrow={false}
                ref={warningPopupRef}
                contentStyle={getPopupContentStyle(theme, isMobile)}
                onOpen={handleInputFocus}
                onClose={handlePopupConfirm}
                open={isPopupVisible}
              >
                <PopupContainerStyled>
                  <Typography>This ticket is closed.</Typography>
                  <Typography>Do you want to send a message and reopen the ticket?</Typography>
                  <Button onClick={handlePopupConfirm}>Ok</Button>
                </PopupContainerStyled>
              </Popup>
              {previewImage && (
                <InputImageWrapStyled>
                  <img
                    src={previewImage}
                    alt="Preview"
                    style={{ maxHeight: '100px', maxWidth: '100%' }}
                  />
                  <CloseIconBtnStyled
                    variant="transparent"
                    icon={<CloseIcon />}
                    title="Remove image"
                    onClick={handleRemoveImage}
                  />
                </InputImageWrapStyled>
              )}
              <StyledDragDropInput {...getInputProps()} />
            </InputMessageWrapStyled>
            <SendMessageBtnStyled
              aria-label="Create message"
              variant="outline"
              icon={<SendIcon />}
              title="Send a message"
            />
          </FormStyled>
        </FormProvider>
      )}
    </>
  );
};

export default MessageForm;
